import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Divider, Row, Col } from 'antd'
import Appointment from 'model/appointments/appointment'
import AsSoonAsPossible from 'model/appointments/timeSlots/asSoonAsPossible'
import SpecificTime from 'model/appointments/timeSlots/specificTime'
import TimeBlock from 'model/appointments/timeSlots/timeBlock'
import TimeSlotsModal from 'components/TimeSlotsModal'
import { Label } from 'pages/Ticket/styled'
import SuggestSlot from 'pages/Ticket/components/RequestData/Appointment/SuggestSlot'
import { Button } from 'ui/styled'
import { GREY_100, GREEN_DARK } from 'ui/constants'
import useAppointmentUpdate from 'apollo/mutations/APPOINTMENT_UPDATE'
import { getDateFormatYMD } from 'utils/helpers/dateHelper'
import { StyledRow, DeleteIcon, SuggestedRow, SuggestedCol } from '../styled'
import { Time_Slot_Kind } from 'generated/types'

const TimeSlots = ({
  appointment,
  timeSlots = [],
  setTimeSlots = () => {},
  setFieldsValue = () => {},
  locationUuid,
  isForm = false
}) => {
  const [showSlotsModal, setShowSlotsModal] = useState(false)

  const { onAppointmentUpdate } = useAppointmentUpdate()

  appointment.time_slots = timeSlots

  const {
    archived,
    hasAsapSlot,
    hasSpecificSlot,
    hasTimeBlockSlot,
    hasSuggestedSlot,
    hasNonRequestedSlots,
    shouldBlockUI
  } = appointment

  const asapSlot = appointment.getAsapSlot
  const specificSlots = appointment.getSpecificSlots
  const timeBlockSlots = appointment.getTimeBlockSlots
  const nonRequestedSlots = appointment.getNonRequestedSlots

  const approveBtnStyle = {
    color: archived ? GREY_100 : GREEN_DARK,
    border: `1px solid ${archived ? GREY_100 : GREEN_DARK}`
  }

  const onAddNew = values => {
    const { kind, days, date, s_time, e_time } = values
    if (!kind) return null

    const timeData =
      kind !== Time_Slot_Kind.Asap
        ? Appointment.getTimeSlotBoundaries(date, s_time, e_time)
        : {}
    const { start, start_time, end, end_time } = timeData

    let newSlot = {
      kind,
      ...(days && { days }),
      ...(date && { date: getDateFormatYMD(date) }),
      ...(typeof start === 'number' && { start }),
      ...(start_time && { start_time }),
      ...(typeof end === 'number' && { end }),
      ...(end_time && { end_time })
    }

    if (kind === Time_Slot_Kind.Asap) newSlot = new AsSoonAsPossible(newSlot)
    if (kind === Time_Slot_Kind.Specific) newSlot = new SpecificTime(newSlot)
    if (kind === Time_Slot_Kind.TimeBlock) newSlot = new TimeBlock(newSlot)

    setTimeSlots([...timeSlots, newSlot])
    if (isForm) setFieldsValue({ preferred_timeslots: [...timeSlots, newSlot] })
    setShowSlotsModal(false)
    return true
  }

  const removeSlot = slot => {
    if (isForm) {
      setTimeSlots(timeSlots.filter(item => item !== slot))
      setFieldsValue({
        preferred_timeslots: timeSlots.filter(item => item !== slot)
      })
    } else {
      setTimeSlots(
        timeSlots.map(item => {
          if (item === slot) {
            slot.remove(true)
            return slot
          }
          return item
        })
      )
    }
  }

  const onRenderButton = async (slot, to, params) => {
    await onAppointmentUpdate({
      id: appointment.id,
      attributes: {
        with: to,
        time_slots_attributes: [
          {
            ...slot.toObject,
            ...params
          }
        ]
      }
    })
  }

  const renderButton = (
    label,
    slot,
    to,
    params = {},
    dataTest,
    style = false
  ) => (
    <Button
      btntype={style ? 'custom' : 'danger'}
      onClick={() => onRenderButton(slot, to, params)}
      disabled={archived}
      margin="0 0 0 16px"
      data-test={dataTest}
      {...style}
    >
      {label}
    </Button>
  )

  return (
    <>
      <Label required>Requested Times</Label>
      {hasAsapSlot && (
        <>
          <span>{asapSlot.description}</span>
          <Button
            onClick={() => removeSlot(asapSlot)}
            disabled={hasSuggestedSlot || shouldBlockUI}
            btnsize="icon"
            btntype="icon"
            margin="0 0 0 16px"
            data-test="appointmentRemoveSlotBtn"
          >
            <DeleteIcon />
          </Button>
        </>
      )}

      {hasSpecificSlot && (
        <>
          <Label>Specific Time</Label>
          {specificSlots.map(slot => (
            <Row gutter={16} style={{ maxWidth: 500 }}>
              <Col span={5}>{slot.weekDay}</Col>
              <Col span={4}>{slot.monthDay}</Col>
              <Col span={8}>{slot.timeRange}</Col>
              <Col span={1}>
                <Button
                  onClick={() => removeSlot(slot)}
                  disabled={hasSuggestedSlot || shouldBlockUI}
                  btnsize="icon"
                  btntype="icon"
                  margin="0 0 0 16px"
                  data-test="appointmentRemoveSlotBtn"
                >
                  <DeleteIcon />
                </Button>
              </Col>
            </Row>
          ))}
        </>
      )}

      {hasTimeBlockSlot && (
        <>
          <Label>Time Block</Label>
          {timeBlockSlots.map(slot => (
            <div key={slot.id}>
              <span>{slot.description}</span>
              <Button
                onClick={() => removeSlot(slot)}
                disabled={hasSuggestedSlot || shouldBlockUI}
                btnsize="icon"
                btntype="icon"
                margin="0 0 0 16px"
                data-test="appointmentRemoveSlotBtn"
              >
                <DeleteIcon />
              </Button>
            </div>
          ))}
        </>
      )}

      <StyledRow gutter={16} style={{ marginTop: 16 }}>
        <Col span={8}>
          <Button
            onClick={() => setShowSlotsModal(true)}
            disabled={hasAsapSlot || hasSuggestedSlot || shouldBlockUI}
            btntype="link"
            data-test="appointmentAddNewSlotBtn"
          >
            + Add New Slot
          </Button>
        </Col>
      </StyledRow>

      {hasNonRequestedSlots && (
        <>
          <Label>Suggested</Label>
          {nonRequestedSlots.map(slot => (
            <SuggestedRow style={{ maxWidth: 600 }}>
              <SuggestedCol span={4}>{slot.weekDay}</SuggestedCol>
              <SuggestedCol span={2}>{slot.monthDay}</SuggestedCol>
              <SuggestedCol span={5}>{slot.displayTime}</SuggestedCol>
              <Col span={10}>
                {(slot.isPending || slot.isSuggested) &&
                  renderButton(
                    'Confirm',
                    slot,
                    'confirm',
                    { kind: 'confirmed' },
                    'ticketAppointmentConfirmSlotBtn',
                    approveBtnStyle
                  )}
                {(slot.isSuggested || slot.isPending) &&
                  renderButton(
                    'Reject',
                    slot,
                    'reject',
                    { delete: true },
                    'ticketAppointmentRejectSlotBtn'
                  )}
                {slot.isConfirmed &&
                  renderButton(
                    'Cancel',
                    slot,
                    'cancel',
                    { kind: 'cancelled' },
                    'ticketAppointmentCancelSlotBtn'
                  )}
                {slot.isCanceledTime &&
                  renderButton(
                    'Finish Cancellation',
                    slot,
                    'finish_cancellation',
                    { delete: true },
                    'ticketAppointmentFinishCancellationBtn'
                  )}
              </Col>
            </SuggestedRow>
          ))}
        </>
      )}

      {appointment &&
        appointment.isAppointment &&
        appointment.shouldAllowSuggest && (
          <StyledRow gutter={16}>
            <Col span={24}>
              <SuggestSlot
                appointment={appointment}
                locationUuid={locationUuid}
              />
            </Col>
          </StyledRow>
        )}

      <Divider />

      <TimeSlotsModal
        visible={showSlotsModal}
        shouldBlockAsap={hasTimeBlockSlot || hasSpecificSlot}
        onCancel={() => setShowSlotsModal(false)}
        onOk={onAddNew}
      />
    </>
  )
}

TimeSlots.propTypes = {
  appointment: PropTypes.shape(Appointment.shape).isRequired,
  timeSlots: PropTypes.array,
  setTimeSlots: PropTypes.func,
  setFieldsValue: PropTypes.func,
  isForm: PropTypes.bool,
  locationUuid: PropTypes.string.isRequired
}

export default TimeSlots
